import { notesApi } from '@/lib/notesApi';
import { lineRate, rateLabel, purityPctOf } from '@/lib/tagPricing';
import { tagMaterials } from '@/components/jewellery/JewelleryTagViewer';
import { catOf, purityLabel } from '@/lib/tagSummary';
import { placeSep } from '@/lib/tagMatHandling';

// Whole rupees, rounded like Excel's ROUND (15 significant digits, so 36.05 x 370 = 13338.5 -> 13339).
const r0 = (n) => Math.round(Number((Number(n) || 0).toPrecision(15)));
const natural = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' });

// Editable draft of one piece for the sale: rates pre-filled from the card / gold rate.
export function draftItem(d, card) {
  return {
    data: d,
    lines: placeSep([
      ...tagMaterials(d, 'priced').map((m) => ({ key: m.key, code: m.code || '', label: rateLabel(m), type: catOf(m), ct: Number(m.carats || 0), pcs: m.hidePieces ? 0 : Number(m.pieces || 0), rate: m.zeroRate ? 0 : d._raw?.alter_rates?.[String(m.code || '').toUpperCase()] ?? lineRate(d, m, card) ?? '' })),
    ], // Separate weights (e.g. Dak Wt) placed by the rule's position; 'zero' = not charged (rate 0).
    (d.separate || []).filter((s) => s.price !== 'none').map((s) => ({ id: s.id, key: s.key, label: s.label, type: 'sep', unit: s.unit, ct: Number(Number(s.qty).toFixed(3)), pcs: 0, rate: s.price === 'zero' ? 0 : (card?.lines || {})[s.label] ?? '' }))),
    labourRate: card?.labour ?? '',
    charges: (d.charges || []).map((c) => ({ name: c.name, amount: c.amount ?? '' })),
  };
}

// Draft → saved item with every amount worked out (gold, raw materials, labour, charges, total).
export function priceDraft(x, gold100) {
  const d = x.data;
  const pct = purityPctOf(d) || 0;
  const net = Number(Number(d.net || 0).toFixed(3));
  // A piece's own gold rate (from its approval) wins over the 100% rate x purity.
  const goldRate = x.goldRate !== undefined ? r0(x.goldRate) : gold100 && pct ? r0((gold100 * pct) / 100) : 0;
  const gold = r0(net * goldRate);
  const mats = { dia: { ct: 0, pcs: 0, amt: 0 }, stone: { ct: 0, pcs: 0, amt: 0 }, pearl: { ct: 0, pcs: 0, amt: 0 }, other: { ct: 0, pcs: 0, amt: 0 } };
  const lines = x.lines.map((l) => {
    const amt = r0(l.ct * r0(l.rate));
    const m = mats[l.type]; if (m) { m.ct += l.ct; m.pcs += l.pcs; m.amt += amt; }
    return { label: l.label, ...(l.code ? { code: l.code } : {}), type: l.type, ct: l.ct, pcs: l.pcs, rate: r0(l.rate), amt, ...(l.unit ? { unit: l.unit } : {}) };
  });
  const sep = (d.separate || []).map((s) => ({ label: s.label, unit: s.unit, qty: Number(Number(s.qty).toFixed(3)), color: s.color }));
  const labour = r0(net * r0(x.labourRate));
  const charges = x.charges.map((c) => ({ name: c.name, amount: r0(c.amount) }));
  const chargesTotal = charges.reduce((t, c) => t + c.amount, 0);
  const total = gold + lines.reduce((t, l) => t + l.amt, 0) + labour + chargesTotal;
  return {
    id: d.id, jewellery_no: d.jewelleryNo || '', jewellery_type: d.jewelleryType || '', purity: purityLabel(d), purity_pct: pct,
    gross: Number(d.gross || 0), net, image: '', mats, lines, sep, gold_rate: goldRate, gold, labour_rate: r0(x.labourRate), labour, charges, charges_total: chargesTotal, total,
  };
}

const blank = () => ({ count: 0, gross: 0, net: 0, gold: 0, labour: 0, charges: 0, total: 0, dia: { ct: 0, pcs: 0, amt: 0 }, stone: { ct: 0, pcs: 0, amt: 0 }, pearl: { ct: 0, pcs: 0, amt: 0 }, other: { ct: 0, pcs: 0, amt: 0 } });
function addItem(s, it) {
  s.count += 1; s.gross += it.gross; s.net += it.net; s.gold += it.gold; s.labour += it.labour; s.charges += it.charges_total; s.total += it.total;
  ['dia', 'stone', 'pearl', 'other'].forEach((k) => { const m = it.mats?.[k]; if (m) { s[k].ct += m.ct; s[k].pcs += m.pcs; s[k].amt += m.amt; } });
}

// Purity-wise + grand total of sold items (returned items are left out by the caller).
export function saleSummary(items) {
  const by = new Map();
  const grand = blank();
  [...items].sort((a, b) => (b.purity_pct || 0) - (a.purity_pct || 0) || natural(a.purity, b.purity)).forEach((it) => {
    if (!by.has(it.purity)) by.set(it.purity, blank());
    addItem(by.get(it.purity), it); addItem(grand, it);
  });
  return { groups: [...by.entries()].map(([label, s]) => ({ label, ...s })), grand };
}

// Every sold (not returned) line of the given sales, flattened with its sale's details.
export const soldLines = (list) => list.flatMap((s) => s.items.filter((i) => !i.returned).map((i) => ({ ...i, sale: s })));

export const loadSales = async (from, to) => (await notesApi.get('/notes/tag-workspace/sales', { params: { date_from: from || undefined, date_to: to || undefined } })).data || [];
export const loadReturns = async () => (await notesApi.get('/notes/tag-workspace/sales/returns')).data || [];
export const editSoldRates = async (items) => (await notesApi.patch('/notes/tag-workspace/sales-rates', { items })).data;
export const createReturn = async (body) => (await notesApi.post('/notes/tag-workspace/sales/returns', body)).data;

// Editable return draft of a sold line: rates pre-filled with the sale rates.
export const draftReturn = (l) => ({ item: l, lines: l.lines.map((x) => ({ ...x })), labourRate: l.labour_rate, charges: (l.charges || []).map((c) => ({ ...c })) });

// Return draft → priced line (same shape as a sold line) at the return rates.
export function priceReturn(x, gold100) {
  const it = x.item;
  const goldRate = x.goldRate !== undefined ? r0(x.goldRate) : gold100 && it.purity_pct ? r0((gold100 * it.purity_pct) / 100) : 0;
  const gold = r0(it.net * goldRate);
  const mats = { dia: { ct: 0, pcs: 0, amt: 0 }, stone: { ct: 0, pcs: 0, amt: 0 }, pearl: { ct: 0, pcs: 0, amt: 0 }, other: { ct: 0, pcs: 0, amt: 0 } };
  const lines = x.lines.map((l) => {
    const amt = r0(l.ct * r0(l.rate));
    const m = l.type === 'sep' ? null : mats[l.type] || mats.other; if (m) { m.ct += l.ct; m.pcs += l.pcs; m.amt += amt; }
    return { label: l.label, type: l.type, ct: l.ct, pcs: l.pcs, rate: r0(l.rate), amt, ...(l.unit ? { unit: l.unit } : {}) };
  });
  const labour = r0(it.net * r0(x.labourRate));
  const charges = x.charges.map((c) => ({ name: c.name, amount: r0(c.amount) }));
  const chargesTotal = charges.reduce((t, c) => t + c.amount, 0);
  return { id: it.id, jewellery_no: it.jewellery_no, jewellery_type: it.jewellery_type, purity: it.purity, purity_pct: it.purity_pct, gross: it.gross, net: it.net, image: '', page_no: it.page_no || '',
    mats, lines, sep: it.sep || [], gold_rate: goldRate, gold, labour_rate: r0(x.labourRate), labour, charges, charges_total: chargesTotal, total: gold + lines.reduce((t, l) => t + l.amt, 0) + labour + chargesTotal };
}

// Gold 999 break-up: fine gold grams (net × purity), gold value, other materials & misc value, total.
export function fineGold(lines) {
  let fg = 0; let gold = 0; let total = 0; const rates = new Set();
  lines.forEach((l) => {
    fg += (Number(l.net) || 0) * (Number(l.purity_pct) || 0) / 100;
    gold += l.gold || 0; total += l.total || 0;
    if (l.sale?.gold_rate_100) rates.add(l.sale.gold_rate_100);
  });
  const rate = rates.size === 1 ? [...rates][0] : (fg ? Math.round(gold / fg) : 0);
  return { fg: Number(fg.toFixed(3)), gold, other: total - gold, total, rate, mixed: rates.size > 1 };
}
